/**
 * Par de claves del dispositivo (T017; implementation-notes 002 §4, research R-2).
 *
 * ECDSA P-256 con `extractable: false` y uso `['sign']`: la clave privada nunca puede exportarse.
 * Se persiste con `./storage.ts` (IndexedDB `fermin-device`) y se cachea en memoria durante la
 * sesión de la página. La clave pública sí es exportable (WebCrypto lo garantiza) y de su SPKI DER
 * se deriva el `device_id` (`./sign.ts`).
 */
import { PublicKeyJwkSchema, type PublicKeyJwk } from '@fermin/contracts';

import { DEVICE_KEY_ALGORITHM, deviceIdFromPublicKey } from './sign';
import { getDeviceKeyStore } from './storage';

export const DEVICE_KEY_USAGES: readonly KeyUsage[] = ['sign'];

let cachedPair: CryptoKeyPair | undefined;
let cachedDeviceId: string | undefined;
let loading: Promise<CryptoKeyPair | undefined> | undefined;
let creating: Promise<CryptoKeyPair> | undefined;

/** Par persistido, si existe. Comparte una única lectura en vuelo entre llamadas concurrentes. */
export async function getKeyPair(): Promise<CryptoKeyPair | undefined> {
  if (cachedPair) {
    return cachedPair;
  }
  loading ??= getDeviceKeyStore()
    .get()
    .then((pair) => {
      cachedPair = pair;
      return pair;
    })
    .finally(() => {
      loading = undefined;
    });
  return loading;
}

/** Devuelve el par existente o genera y persiste uno nuevo (una sola generación aunque haya carreras). */
export async function getOrCreateKeyPair(): Promise<CryptoKeyPair> {
  const existing = await getKeyPair();
  if (existing) {
    return existing;
  }
  creating ??= (async () => {
    const pair = await crypto.subtle.generateKey(DEVICE_KEY_ALGORITHM, false, [
      ...DEVICE_KEY_USAGES,
    ]);
    await getDeviceKeyStore().set(pair);
    cachedPair = pair;
    cachedDeviceId = undefined;
    return pair;
  })().finally(() => {
    creating = undefined;
  });
  return creating;
}

/** `true` si este dispositivo ya tiene clave (no implica que esté enrolada en el servidor). */
export async function hasKey(): Promise<boolean> {
  return (await getKeyPair()) !== undefined;
}

/**
 * JWK público estricto `{ kty, crv, x, y }` para `DeviceEnrollBody.public_key_jwk`. Crea la clave si
 * no existe (es el primer paso del enrolamiento). Validado con `PublicKeyJwkSchema`.
 */
export async function exportPublicJwk(): Promise<PublicKeyJwk> {
  const pair = await getOrCreateKeyPair();
  const jwk = await crypto.subtle.exportKey('jwk', pair.publicKey);
  return PublicKeyJwkSchema.parse({ kty: jwk.kty, crv: jwk.crv, x: jwk.x, y: jwk.y });
}

/** `dev_…` derivado de la clave pública actual (memoizado). Crea la clave si no existe. */
export async function getDeviceId(): Promise<string> {
  const pair = await getOrCreateKeyPair();
  cachedDeviceId ??= await deviceIdFromPublicKey(pair.publicKey);
  return cachedDeviceId;
}

/**
 * Borra la clave (memoria + almacenamiento). Solo soporte/pruebas y el caso
 * `409 CONFLICT` de `enroll` (clave ya registrada tras una revocación): la siguiente llamada a
 * `getOrCreateKeyPair()` genera una nueva identidad de dispositivo.
 */
export async function resetKey(): Promise<void> {
  await getDeviceKeyStore().clear();
  cachedPair = undefined;
  cachedDeviceId = undefined;
}
